import { useEffect, useState } from 'react';

/** How often an open tab asks whether a newer build has been deployed. */
const CHECK_EVERY_MS = 5 * 60 * 1000;

const BUNDLE = /\/assets\/index-[\w-]+\.js/;

/** The hashed bundle this tab is running; null in development, which has none. */
function runningBundle(): string | null {
  for (const script of document.querySelectorAll<HTMLScriptElement>('script[type="module"]')) {
    const found = BUNDLE.exec(script.src);
    if (found !== null) return found[0];
  }
  return null;
}

/** The bundle the server would hand a fresh load; null when it cannot say. */
async function deployedBundle(): Promise<string | null> {
  try {
    const response = await fetch('/', { cache: 'no-store', credentials: 'same-origin' });
    if (!response.ok) return null;
    return BUNDLE.exec(await response.text())?.[0] ?? null;
  } catch {
    return null;
  }
}

/**
 * Whether a newer build is deployed than the one this tab runs: every build
 * names its bundle by content hash, so a different name in the served page is
 * a different build. Asked every few minutes and whenever the tab comes back
 * into view.
 */
function useNewVersion(): boolean {
  const [stale, setStale] = useState(false);
  useEffect(() => {
    const running = runningBundle();
    if (running === null) return;
    let cancelled = false;
    const check = async (): Promise<void> => {
      const deployed = await deployedBundle();
      if (!cancelled && deployed !== null && deployed !== running) setStale(true);
    };
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void check();
    };
    const timer = setInterval(() => void check(), CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);
  return stale;
}

/**
 * "A new version is available — Reload". Never reloads by itself: the page
 * may hold an edit not yet saved.
 */
export function NewVersionBanner(): React.JSX.Element | null {
  const stale = useNewVersion();
  const [dismissed, setDismissed] = useState(false);
  if (!stale || dismissed) return null;
  return (
    <div className="new-version-banner" role="status">
      A new version is available.
      <button className="button small primary" type="button" onClick={() => location.reload()}>
        Reload
      </button>
      <button className="button small" type="button" onClick={() => setDismissed(true)}>
        Later
      </button>
    </div>
  );
}
